import React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Users, Search as SearchIcon, Megaphone, Wallet, Shield, ArrowLeft, FolderTree, FileText, Tag, Star, Palette, LogOut, UserCog, Truck, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const sections = [
  { title: 'Overview', items: [
    { to: '/admin', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  ]},
  { title: 'Catalog', items: [
    { to: '/admin/products', label: 'Products', Icon: Package, perm: 'products' },
    { to: '/admin/media', label: 'Media Library', Icon: ImageIcon, perm: 'products' },
    { to: '/admin/categories', label: 'Categories', Icon: FolderTree, perm: 'categories' },
    { to: '/admin/reviews', label: 'Reviews', Icon: Star, perm: 'reviews' },
    { to: '/admin/coupons', label: 'Discounts', Icon: Tag, perm: 'coupons' },
    { to: '/admin/pages', label: 'Pages (CMS)', Icon: FileText, perm: 'pages' },
  ]},
  { title: 'Sales', items: [
    { to: '/admin/orders', label: 'Orders', Icon: ShoppingCart, perm: 'orders' },
    { to: '/admin/crm', label: 'CRM — Pipeline', Icon: Users, perm: 'customers' },
  ]},
  { title: 'Marketing', items: [
    { to: '/admin/marketing', label: 'Social Posts', Icon: Megaphone, perm: 'marketing' },
    { to: '/admin/seo', label: 'SEO', Icon: SearchIcon, perm: 'seo' },
  ]},
  { title: 'Settings', items: [
    { to: '/admin/branding', label: 'Branding & Site', Icon: Palette, perm: 'branding' },
    { to: '/admin/payments', label: 'Payments & Crypto', Icon: Wallet, perm: 'payments' },
    { to: '/admin/shipping', label: 'Shipping & Vouchers', Icon: Truck, perm: 'payments' },
    { to: '/admin/security', label: 'Security & Hosting', Icon: Shield, perm: 'security' },
    { to: '/admin/users', label: 'Users & Roles', Icon: UserCog, perm: 'users' },
  ]},
];

const flatNav = sections.flatMap((s) => s.items);

const AdminLayout = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const active = flatNav.find((n) => n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));
  const handleLogout = () => { logout(); navigate('/admin/login', { replace: true }); };
  return (
    <div className="min-h-screen bg-black flex">
      <aside className="w-[260px] shrink-0 bg-zinc-950 border-r border-zinc-900 sticky top-0 h-screen overflow-auto">
        <div className="p-5 border-b border-zinc-900">
          <Link to="/" className="flex items-center gap-3">
            <img data-testid="admin-sidebar-brand-logo" src="https://customer-assets.emergentagent.com/job_vape-shop-crypto/artifacts/9lx9auiu_logo.webp" alt="We Supply Smoke" className="h-10 w-auto object-contain rounded-md" />
            <div>
              <p className="serif text-[#d4af37] text-xs tracking-[0.25em]">WE SUPPLY SMOKE</p>
              <p className="text-zinc-500 text-xs">Elite Console</p>
            </div>
          </Link>
        </div>
        <nav className="p-3 space-y-5">
          {sections.map((sec) => {
            const visibleItems = sec.items.filter((it) => !it.perm || hasPermission(it.perm));
            if (visibleItems.length === 0) return null;
            return (
              <div key={sec.title}>
                <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] px-2 mb-2">{sec.title}</p>
                <div className="space-y-0.5">
                  {visibleItems.map(({ to, label, Icon, end }) => (
                    <NavLink key={to} to={to} end={end} data-testid={`nav-${to.split('/').pop() || 'dashboard'}`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${isActive ? 'bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]' : 'text-zinc-300 hover:bg-zinc-900 hover:text-[#d4af37]'}`}>
                      <Icon className="h-4 w-4" /> {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-4 mt-3 border-t border-zinc-900">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-[#d4af37] text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to storefront
          </Link>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="h-16 border-b border-zinc-900 px-8 flex items-center justify-between bg-black sticky top-0 z-30">
          <h1 className="serif-italic text-2xl text-[#d4af37]">{active?.label || 'Admin'}</h1>
          <div className="flex items-center gap-4">
            <div className="text-zinc-500 text-sm hidden md:block">
              <span className="text-zinc-300">{user?.name || user?.email}</span>
              <span className="text-zinc-600 mx-2">·</span>
              <span className="text-[#d4af37] uppercase tracking-wider text-[10px]">{user?.role}</span>
            </div>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-zinc-400 hover:text-rose-400 text-sm border border-zinc-800 rounded px-3 py-1.5 transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
